import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../utils/axios';
import { setCurrencyHelpers } from '../utils/currency';
import { useAuth } from './AuthContext';

const CURRENCIES = {
  INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  USD: { symbol: '$', code: 'USD', name: 'US Dollar' },
  EUR: { symbol: '€', code: 'EUR', name: 'Euro' },
  GBP: { symbol: '£', code: 'GBP', name: 'British Pound' },
  AED: { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham' },
  SGD: { symbol: 'S$', code: 'SGD', name: 'Singapore Dollar' },
  AUD: { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
  NZD: { symbol: 'NZ$', code: 'NZD', name: 'New Zealand Dollar' },
  CAD: { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
  JPY: { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
  THB: { symbol: '฿', code: 'THB', name: 'Thai Baht' },
  MYR: { symbol: 'RM', code: 'MYR', name: 'Malaysian Ringgit' },
  LKR: { symbol: 'Rs', code: 'LKR', name: 'Sri Lankan Rupee' },
};

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState({ code: 'INR', symbol: '₹' });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let active = true;
    API.get('/settings').then(({ data }) => {
      if (!active) return;
      const code = data.currency || 'INR';
      const sym = data.currencySymbol || '₹';
      const fmt = (v) => `${sym}${v != null ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '0'}`;
      setCurrency({ code, symbol: sym });
      setSettings(data);
      setCurrencyHelpers(fmt, sym, code);
    }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAuthenticated]);

  const updateCurrency = useCallback(async (code) => {
    try {
      const { data } = await API.put('/settings', { currency: code });
      const sym = data.currencySymbol;
      setCurrency({ code: data.currency, symbol: sym });
      setSettings(data);
      const fmt = (v) => `${sym}${v != null ? Number(v).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2}) : '0'}`;
      setCurrencyHelpers(fmt, sym, data.currency);
      return data;
    } catch (err) {
      throw err;
    }
  }, []);

  const formatMoney = useCallback((amount) => {
    if (amount === null || amount === undefined) return `${currency.symbol}0`;
    const formatted = Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return `${currency.symbol}${formatted}`;
  }, [currency]);

  const moneyProps = useCallback((prefix) => ({
    prefix: currency.symbol,
    formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    parser: (v) => v.replace(/,/g, ''),
  }), [currency]);

  return (
    <CurrencyContext.Provider value={{
      currency, settings, loading, updateCurrency, formatMoney, moneyProps,
      currencies: Object.values(CURRENCIES),
      CURRENCIES,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
